
import React from 'react';
import { IntegrationFlow, Language } from '../../types';
import { NODE_WIDTH, GRID_SIZE } from './constants';

interface FlowsCanvasProps {
  language: Language;
  activeFlow: IntegrationFlow;
  isSimulating: boolean;
  pan: { x: number; y: number };
  zoom: number;
  selectedNodeId: string | null;
  connectionStart: string | null;
  mousePos: { x: number; y: number };
  canvasRef: React.RefObject<HTMLDivElement>;
  onNodeMouseDown: (e: React.MouseEvent, nodeId: string) => void;
  onCanvasMouseDown: (e: React.MouseEvent | React.TouchEvent) => void;
  onPortMouseDown: (e: React.MouseEvent, nodeId: string) => void;
  onPortMouseUp: (e: React.MouseEvent, nodeId: string) => void;
  onZoom: (delta: number) => void;
  onResetView: () => void;
}

const getSmartEdgePath = (sx: number, sy: number, tx: number, ty: number, isRtl: boolean) => {
  const sourceX = isRtl ? sx : sx + NODE_WIDTH;
  const sourceY = sy + 50;
  const targetX = isRtl ? tx + NODE_WIDTH : tx;
  const targetY = ty + 50;
  const deltaX = Math.abs(targetX - sourceX);
  const deltaY = Math.abs(targetY - sourceY);
  const dir = isRtl ? -1 : 1;
  let cp1X, cp1Y, cp2X, cp2Y;

  if ((!isRtl && targetX < sourceX) || (isRtl && targetX > sourceX)) {
     const loopSize = Math.max(100, deltaY * 0.5);
     cp1X = sourceX + (loopSize * dir);
     cp1Y = sourceY + (deltaY > 0 ? loopSize : -loopSize);
     cp2X = targetX - (loopSize * dir);
     cp2Y = targetY;
  } else {
     const curvature = Math.max(deltaX * 0.5, 80);
     cp1X = sourceX + (curvature * dir);
     cp1Y = sourceY;
     cp2X = targetX - (curvature * dir);
     cp2Y = targetY;
  }
  return `M ${sourceX} ${sourceY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${targetX} ${targetY}`;
};

const FlowsCanvas: React.FC<FlowsCanvasProps> = ({
  language, activeFlow, isSimulating, pan, zoom, selectedNodeId,
  connectionStart, mousePos, canvasRef, onNodeMouseDown, onCanvasMouseDown,
  onPortMouseDown, onPortMouseUp, onZoom, onResetView
}) => {
  const isRtl = language === 'ar';

  return (
    <main ref={canvasRef} className="flex-1 relative bg-[#08080a] overflow-hidden cursor-grab active:cursor-grabbing" onMouseDown={onCanvasMouseDown} onTouchStart={onCanvasMouseDown}>
       <div className="absolute inset-0 pointer-events-none opacity-20" style={{ backgroundImage: 'radial-gradient(#334155 1px, transparent 1px)', backgroundSize: `${GRID_SIZE * zoom}px ${GRID_SIZE * zoom}px`, backgroundPosition: `${pan.x}px ${pan.y}px` }} />
       <div className="w-full h-full origin-top-left" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
          <svg className="absolute inset-0 overflow-visible pointer-events-none w-full h-full" style={{ zIndex: 0 }}>
             <defs><marker id="head" orient="auto" markerWidth="6" markerHeight="6" refX="3" refY="3"><path d="M0,0 L6,3 L0,6 Z" fill="#64748b" /></marker></defs>
             {activeFlow.edges.map(edge => {
                const s = activeFlow.nodes.find(n => n.id === edge.source);
                const t = activeFlow.nodes.find(n => n.id === edge.target);
                if (!s || !t) return null;
                const d = getSmartEdgePath(s.position.x, s.position.y, t.position.x, t.position.y, isRtl);
                return (<g key={edge.id}><path d={d} stroke="#1e293b" strokeWidth="6" fill="none" /><path d={d} stroke="#64748b" strokeWidth="2" fill="none" markerEnd="url(#head)" />{isSimulating && <circle r="4" fill="#10b981"><animateMotion dur="2s" repeatCount="indefinite" path={d} /></circle>}</g>);
             })}
             {connectionStart && <path d={getSmartEdgePath(activeFlow.nodes.find(n => n.id === connectionStart)!.position.x, activeFlow.nodes.find(n => n.id === connectionStart)!.position.y, (mousePos.x - pan.x) / zoom, (mousePos.y - pan.y) / zoom - 50, isRtl)} stroke="#10b981" strokeWidth="2" strokeDasharray="5,5" fill="none" />}
          </svg>
          {activeFlow.nodes.map(node => {
             const isSelected = selectedNodeId === node.id;
             const color = node.data.color || 'slate';
             return (
                <div key={node.id} style={{ transform: `translate(${node.position.x}px, ${node.position.y}px)`, width: NODE_WIDTH }} className={`flow-interactive absolute z-10 flex flex-col bg-[#1a1a20] rounded-2xl border-2 shadow-2xl transition-shadow duration-200 group ${isSelected ? `border-${color}-500 shadow-[0_0_30px_rgba(var(--${color}-500),0.2)]` : 'border-slate-800 hover:border-slate-600'}`} onMouseDown={(e) => onNodeMouseDown(e, node.id)}>
                   <div className={`h-1.5 w-full rounded-t-xl bg-gradient-to-r from-${color}-500 to-${color}-700`}></div>
                   <div className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl bg-${color}-500/10 flex items-center justify-center text-${color}-500 text-lg shadow-inner shrink-0`}><i className={`fas ${node.data.icon}`}></i></div>
                      <div className="flex-1 min-w-0"><h3 className="text-xs font-black text-white truncate">{node.data.label}</h3><p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">{node.data.subLabel}</p></div>
                      <div className={`w-2 h-2 rounded-full ${isSimulating ? 'bg-emerald-500 animate-pulse' : 'bg-slate-700'}`}></div>
                   </div>
                   {node.type !== 'trigger' && <div className={`flow-interactive absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#08080a] border-2 border-slate-600 hover:border-white hover:scale-110 transition-all z-20 cursor-crosshair flex items-center justify-center ${isRtl ? '-right-3' : '-left-3'}`} onMouseUp={(e) => onPortMouseUp(e, node.id)}><div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div></div>}
                   {node.type !== 'output' && <div className={`flow-interactive absolute top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#08080a] border-2 border-slate-600 hover:border-emerald-500 hover:scale-110 transition-all z-20 cursor-crosshair flex items-center justify-center ${isRtl ? '-left-3' : '-right-3'}`} onMouseDown={(e) => onPortMouseDown(e, node.id)}><div className="w-1.5 h-1.5 rounded-full bg-slate-500 group-hover:bg-emerald-500"></div></div>}
                </div>
             );
          })}
       </div>
       <div className="absolute bottom-6 left-6 flex flex-col gap-2 z-40">
          <button onClick={() => onZoom(0.1)} className="w-10 h-10 bg-[#1a1a20] border border-white/10 rounded-xl text-slate-400 hover:text-white flex items-center justify-center transition-colors"><i className="fas fa-plus"></i></button>
          <button onClick={() => onZoom(-0.1)} className="w-10 h-10 bg-[#1a1a20] border border-white/10 rounded-xl text-slate-400 hover:text-white flex items-center justify-center transition-colors"><i className="fas fa-minus"></i></button>
          <button onClick={onResetView} className="w-10 h-10 bg-[#1a1a20] border border-white/10 rounded-xl text-slate-400 hover:text-white flex items-center justify-center transition-colors"><i className="fas fa-expand"></i></button>
       </div>
    </main>
  );
};
export default FlowsCanvas;
